// Main GigShip Web Application
// src/App.jsx - COMPLETE WITH ALL ROUTES
import { useTranslation } from 'react-i18next';
import React, { useEffect, useState, Suspense } from 'react';
import { Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { HelmetProvider } from 'react-helmet-async';
import { useDispatch } from 'react-redux';
import socketService from './services/socket';
import { useAuth } from './contexts/AuthContext';
import { ROUTES } from './utils/constants';
import { usePWA } from './hooks/usePWA';
import DashboardLayout from './components/DashboardLayout';
import AdminLayout from './components/AdminLayout';
import InstallPrompt from './components/InstallPrompt';
import NotFound from './components/NotFound';
import CookieConsent from './components/DataPolicy';
import { WifiOff } from 'lucide-react';
import OAuthCallback from './pages/OAuthCallback';
import ServiceWorkerUpdate from './components/ServiceWorkerUpdate';
import DashboardErrorBoundary from './components/DashboardErrorBoundary';
import OnboardingAccessGate from './components/OnboardingAccessGate';
import ErrorBoundary from './components/ErrorBoundary';
import { setUserOnlineStatus } from './store/slices/messagesSlice';
import * as Sentry from '@sentry/react';
import { OverlayQueueProvider } from './contexts/OverlayQueueContext';

// ------------------ Direct Imports ------------------
import Messages from './pages/dashboard/Messages';
import Wallet from './pages/dashboard/Wallet';
import Analytics from './pages/dashboard/Analytics';
import Referrals from './pages/dashboard/Referrals';
import CookiesPolicyPage from './pages/static/CookiesPolicyPage';
import FeaturedSubscription from './pages/FeaturedSubscription';
import FeaturedManagement from './pages/admin/FeaturedManagement';

// Header & Footer Components
import PublicHeader from './components/PublicHeader';
import PublicFooter from './components/PublicFooter';
import PWAFooter from './components/PWAFooter';

// ------------------ Lazy Loaded Pages ------------------
const Home = React.lazy(() => import('./pages/Home'));
const GigCreation = React.lazy(() => import('./pages/GigCreation'));
const GigEdit = React.lazy(() => import('./pages/GigEdit'));
const RequestEdit = React.lazy(() => import('./pages/RequestEdit'));
const GigListing = React.lazy(() => import('./pages/GigListing'));
const GigDetail = React.lazy(() => import('./pages/GigDetail'));
const UserProfile = React.lazy(() => import('./pages/UserProfile'));
const OrderCreation = React.lazy(() => import('./pages/OrderCreation'));
const PostJobRequest = React.lazy(() => import('./pages/PostJobRequest'));
const Giggers = React.lazy(() => import('./pages/Giggers'));
const SMSDiagnostic = React.lazy(() => import('./pages/SMSDiagnostic'));
const GoLandingPage = React.lazy(() => import('./pages/GoLandingPage'));
const Leaderboard = React.lazy(() => import('./pages/Leaderboard'));
const Reviews = React.lazy(() => import('./pages/dashboard/Reviews'));

// Static Pages
const Support = React.lazy(() => import('./pages/static/Support'));
const Privacy = React.lazy(() => import('./pages/static/Privacy'));
const Terms = React.lazy(() => import('./pages/static/Terms'));
const HowItWorks = React.lazy(() => import('./pages/static/HowItWorks'));
const AboutUs = React.lazy(() => import('./pages/static/AboutUs'));
const FAQ = React.lazy(() => import('./pages/static/FAQ'));
const ReferralProgram = React.lazy(() => import('./pages/static/ReferralProgram'));
const UserLevels = React.lazy(() => import('./pages/static/UserLevels'));
const Verifications = React.lazy(() => import('./pages/static/Verifications'));
const TrustSafety = React.lazy(() => import('./pages/static/TrustSafety'));
const Documentation = React.lazy(() => import('./pages/static/Documentation'));

// Auth Pages
const Login = React.lazy(() => import('./pages/auth/Login'));
const AdminLogin = React.lazy(() => import('./pages/auth/AdminLogin')); // ✅ NEW
const Register = React.lazy(() => import('./pages/auth/Register'));
const VerifyPhone = React.lazy(() => import('./pages/auth/VerifyPhone'));
const ResetPassword = React.lazy(() => import('./pages/auth/ResetPassword'));
const Onboarding = React.lazy(() => import('./pages/Onboarding'));
const VerifyEmail = React.lazy(() => import('./pages/auth/VerifyEmail'));
const VerifyDeviceLogin = React.lazy(() => import('./pages/auth/VerifyDeviceLogin'));
const VerifyParallel = React.lazy(() => import('./pages/auth/VerifyParallel'));
const ReferralRedirect = React.lazy(() => import('./pages/auth/ReferralRedirect'));

// Dashboard
const Dashboard = React.lazy(() => import('./pages/dashboard/Dashboard'));
const OrdersDashboard = React.lazy(() => import('./pages/dashboard/OrdersDashboard'));
const Settings = React.lazy(() => import('./pages/dashboard/Settings'));
const OrderDetail = React.lazy(() => import('./pages/dashboard/OrderDetail'));
const RequestsPage = React.lazy(() => import('./pages/RequestsPage'));
const RequestProposals = React.lazy(() => import('./pages/dashboard/RequestProposals'));
const MyGigs = React.lazy(() => import('./components/MyGigs'));
const MyRequests = React.lazy(() => import('./components/MyRequests'));
const MediatorDashboard = React.lazy(() => import('./pages/dashboard/MediatorDashboard')); // NEW
const SavedGigs = React.lazy(() => import('./pages/SavedGigs'));
const SavedRequests = React.lazy(() => import('./pages/SavedRequests'));

// Issue 9: Forum
const Forum = React.lazy(() => import('./pages/Forum'));
const ThreadDetail = React.lazy(() => import('./components/forum/ThreadDetail'));
const CreateThread = React.lazy(() => import('./components/forum/CreateThread'));
const ForumDashboard = React.lazy(() => import('./pages/dashboard/ForumDashboard')); // NEW

// ✅ NEW: Verification Pages
const IdentityVerification = React.lazy(() => import('./pages/verification/IdentityVerification'));
const FacialVerification = React.lazy(() => import('./pages/verification/FacialVerification'));

// ✅ Route B: Public Voucher Capture (no auth, no layout wrapper)
const VoucherCapture = React.lazy(() => import('./pages/VoucherCapture'));

// Admin
const AdminDashboard = React.lazy(() => import('./pages/admin/AdminDashboard'));
const AdminUsers = React.lazy(() => import('./pages/admin/AdminUsers'));
const AdminOrders = React.lazy(() => import('./pages/admin/AdminOrders'));
const AdminGigs = React.lazy(() => import('./pages/admin/AdminGigs'));
const AdminDisputes = React.lazy(() => import('./pages/admin/AdminDisputes')); // Updated
const AdminEmailDashboard = React.lazy(() => import('./pages/admin/AdminEmailDashboard'));
const AdminEmailAlerts = React.lazy(() => import('./pages/admin/AdminEmailAlerts'));
const AdminDeposits = React.lazy(() => import('./pages/admin/AdminDeposits'));
const AdminPaymentMethods = React.lazy(() => import('./pages/admin/AdminPaymentMethods'));
const AdminOffersDiscounts = React.lazy(() => import('./pages/admin/AdminOffersDiscounts'));
const AdminBonusSettings = React.lazy(() => import('./pages/admin/AdminBonusSettings'));
const AdminVerifications = React.lazy(() => import('./pages/admin/AdminVerifications'));
const AdminAnalytics = React.lazy(() => import('./pages/admin/AdminAnalytics'));
const SuperAdminDashboard = React.lazy(() => import('./pages/admin/SuperAdminDashboard'));
const AdminJobRequests = React.lazy(() => import('./pages/admin/AdminJobRequests'));
const SafetyDashboard = React.lazy(() => import('./pages/admin/SafetyDashboard'));
const CallSettings = React.lazy(() => import('./pages/admin/CallSettings'));
const PayoutSettings = React.lazy(() => import('./pages/admin/PayoutSettings'));
const AdminReferrals = React.lazy(() => import('./pages/admin/AdminReferrals'));
const AdminProfileChangeRequests = React.lazy(() => import('./pages/admin/AdminProfileChangeRequests'));
const AdminOnboarding = React.lazy(() => import('./pages/admin/AdminOnboarding'));
const SystemLogs = React.lazy(() => import('./pages/admin/SystemLogs'));


// =========================================================================
// Loading Screen
// =========================================================================
const LoadingScreen = ({ messageKey = 'common.loading', message = null }) => {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <div className="flex flex-col items-center space-y-6">
        <img
          src="/gigship-silver-crest.gif"
          alt="GigShip"
          className="w-20 h-20"
          onError={(e) => (e.target.style.display = 'none')}
        />
        <div className="w-12 h-12 border-4 border-gray-300 border-t-gray-800 rounded-full animate-spin"></div>
        <p className="text-sm text-gray-600 font-medium">{message || t(messageKey, 'Loading your experience...')}</p>
      </div>
    </div>
  );
};

// =========================================================================
// Protected Route - NO VERIFICATION CHECKS
// =========================================================================
const ProtectedRoute = ({ children, requiredRole }) => {
  const { isAuthenticated, isLoading, hasRole } = useAuth();

  if (isLoading) return <LoadingScreen message="Authenticating..." />;
  if (!isAuthenticated) return <Navigate to={ROUTES.LOGIN} replace />;

  if (requiredRole && !hasRole(requiredRole)) {
    return <Navigate to={ROUTES.DASHBOARD} replace />;
  }

  return children;
};

// Dashboard pages also require the onboarding minimum (verified email + phone,
// profile ≥ 70%, first listing) — see components/OnboardingAccessGate.
const DashboardWrapper = ({ children, requiredRole }) => (
  <ProtectedRoute requiredRole={requiredRole}>
    <OnboardingAccessGate>
      <DashboardErrorBoundary>
        <DashboardLayout>{children || <Outlet />}</DashboardLayout>
      </DashboardErrorBoundary>
    </OnboardingAccessGate>
  </ProtectedRoute>
);

const NavigateWithState = ({ to, replace }) => {
  const location = useLocation();
  return <Navigate to={to} replace={replace} state={location.state} />;
};


// =========================================================================
// Layout Components
// =========================================================================
const AuthLayout = ({ children }) => {
  const { isPWA } = usePWA();
  return (
    <div className="min-h-screen flex flex-col">
      <PublicHeader />
      <main className="flex-grow flex items-center justify-center">{children}</main>
      {isPWA ? <PWAFooter /> : <PublicFooter />}
    </div>
  );
};

const PublicLayoutWrapper = () => {
  const { isPWA } = usePWA();
  return (
    <div className="min-h-screen flex flex-col">
      <PublicHeader />
      <main className="flex-grow">
        <Outlet />
      </main>
      {isPWA ? <PWAFooter /> : <PublicFooter />}
    </div>
  );
};

// Admin Verification
const AdminRoute = ({ children }) => {
  const { user, isLoading } = useAuth();
  if (isLoading) return <LoadingScreen />;
  if (!user || !(user.roles?.includes('admin') || user.roles?.includes('superadmin'))) {
    return <Navigate to="/dashboard" replace />;
  }
  return children;
};

// Super Admin Verification
const SuperAdminRoute = ({ children }) => {
  const { user, isLoading } = useAuth();
  if (isLoading) return <LoadingScreen />;
  if (!user || !user.roles?.includes('superadmin')) {
    return <Navigate to="/admin" replace />;
  }
  return children;
};

// =========================================================================
// Offline Banner Component
// =========================================================================
const OfflineBanner = () => (
  <div className="fixed top-0 left-0 right-0 z-50 bg-yellow-500 text-white p-2 flex items-center justify-center shadow-lg animate-pulse-once">
    <WifiOff className="w-5 h-5 mr-2" />
    <span className="font-semibold text-sm md:text-base">
      You're currently offline. Some features may not be available.
    </span>
  </div>
);

// =========================================================================
// MAIN APP
// =========================================================================
function App() {
  const dispatch = useDispatch();
  const { isAuthenticated, user, token, updateUser } = useAuth();
  const [offline, setOffline] = useState(!navigator.onLine);
  const { isPWA } = usePWA();
  const location = useLocation();

  // -------------------- OFFLINE MANAGEMENT --------------------
  useEffect(() => {
    const handleOnline = () => {
      console.log('✅ Back online');
      setOffline(false);
    };
    const handleOffline = () => {
      console.log('⚠️ Device is offline');
      setOffline(true);
    };
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // -------------------- REFERRAL PERSISTENCE --------------------
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const ref = urlParams.get('ref');
    if (ref) {
      const code = ref.toUpperCase();
      localStorage.setItem('referralCode', code);
      const expires = new Date();
      expires.setTime(expires.getTime() + 30 * 24 * 60 * 60 * 1000); // 30 days
      document.cookie = `referralCode=${code};expires=${expires.toUTCString()};path=/`;
      console.log(`✅ Referral code ${code} persisted for 30 days`);
    }
  }, [location.search]);

  // Safe Tawk.API initialization
  useEffect(() => {
    try {
      if (user && window.Tawk_API) {
        if (typeof window.Tawk_API.visitor === 'function') {
          window.Tawk_API.visitor({
            name: `${user.username || ''} ${user.username || ''}`.trim() || user.username,
            email: user.email,
            hash: user.id
          });

          if (typeof window.Tawk_API.setAttributes === 'function') {
            window.Tawk_API.setAttributes({
              id: user.id,
              hash: user.id
            }, function (error) { });
          }
          console.log('✅ Tawk.API visitor set');
        }
      }
    } catch (error) {
      console.error('❌ Tawk.API error (non-fatal):', error.message);
    }
  }, [user]);

  // Socket Management
  useEffect(() => {
    if (!isAuthenticated || !token) {
      console.log('⛔ Not authenticated, skipping socket connection');
      socketService.disconnect();
      return;
    }

    if (!user?.id) {
      console.log('⚠️ User not loaded yet, waiting...');
      return;
    }

    const hasBasicVerification =
      user?.verificationLevel && user.verificationLevel !== 'none'
        ? true
        : Boolean(user?.emailVerified && user?.phoneVerified);

    if (!hasBasicVerification) {
      console.log('⛔ User not verified, skipping socket connection');
      socketService.disconnect();
      return;
    }

    const freshToken = localStorage.getItem('gigship_token') || token;

    if (!freshToken) {
      console.log('⚠️ No token available, skipping socket connection');
      return;
    }

    console.log('🔌 Initializing socket connection...');
    const timer = setTimeout(() => {
      socketService.connect(hasBasicVerification, freshToken);

      // Issue 1: Use socketService.on() proxy — never touch raw socket.on directly
      socketService.on('modeChanged', ({ activeRole }) => {
        console.log(`📡 Socket event received: modeChanged -> ${activeRole}`);
        updateUser({ activeRole });
      });
      socketService.on('user_active', ({ userId }) => {
        dispatch(setUserOnlineStatus({ userId, isOnline: true }));
      });
      socketService.on('user_inactive', ({ userId }) => {
        dispatch(setUserOnlineStatus({ userId, isOnline: false }));
      });
    }, 400);

    return () => {
      clearTimeout(timer);
      console.log('🔌 Disconnecting socket on unmount');
      socketService.disconnect();
    };
  }, [isAuthenticated, user?.id, user?.verificationLevel, user?.emailVerified, user?.phoneVerified, token]);

  return (
    <HelmetProvider>
      <OverlayQueueProvider>
        <ServiceWorkerUpdate />
        {offline && <OfflineBanner />}
        <Suspense fallback={<LoadingScreen />}>
          <ErrorBoundary>
            <Routes>
              {/* Home Page */}
              <Route
                path={ROUTES.HOME}
                element={
                  <div className="min-h-screen flex flex-col">
                    <PublicHeader />
                    <main className="flex-grow">
                      <Home />
                    </main>
                    {isPWA ? <PWAFooter /> : <PublicFooter />}
                  </div>
                }
              />

              {/* ✅ Route B: Standalone public voucher capture page — no auth, no nav */}
              <Route path="/vouch/:token" element={<VoucherCapture />} />

              {/* Public Layout */}
              <Route element={<PublicLayoutWrapper />}>
                <Route path="/gigs" element={<GigListing />} />
                <Route path="/giggers" element={<Giggers />} />
                <Route path="/leaderboard" element={<Leaderboard />} />
                <Route path="/gig/:gigId" element={<GigDetail />} />
                <Route path="/profile/:username" element={<UserProfile />} />
                <Route path="/support" element={<Support />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/terms" element={<Terms />} />
                <Route path="/how-it-works" element={<HowItWorks />} />
                <Route path="/requests" element={<RequestsPage />} />
                <Route path="/about" element={<AboutUs />} />
                <Route path="/faq" element={<FAQ />} />
                <Route path="/referral-program" element={<ReferralProgram />} />
                <Route path="/user-levels" element={<UserLevels />} />
                <Route path="/verifications" element={<Verifications />} />
                <Route path="/trust-safety" element={<TrustSafety />} />
                <Route path="/cookies-policy" element={<CookiesPolicyPage />} />
                <Route path="/documentation" element={<Documentation />} />
                <Route path="/featured-subscription" element={<FeaturedSubscription />} />
                <Route path={ROUTES.GO} element={<GoLandingPage />} />
                {/* Issue 9: Forum — public listing */}
                <Route path="/forum" element={<Forum />} />
                <Route path="/forum/thread/:threadId" element={<ThreadDetail />} />
              </Route>

              {/* Auth Routes */}
              <Route
                path={ROUTES.LOGIN}
                element={
                  isAuthenticated ? <Navigate to={ROUTES.DASHBOARD} replace /> : <AuthLayout><Login /></AuthLayout>
                }
              />
              {/* ✅ New Admin Login Route */}
              <Route
                path="/admin/login"
                element={
                  isAuthenticated ? <Navigate to="/admin" replace /> : <AdminLogin />
                }
              />
              <Route
                path={ROUTES.REGISTER}
                element={
                  isAuthenticated ? <Navigate to={ROUTES.DASHBOARD} replace /> : <AuthLayout><Register /></AuthLayout>
                }
              />

              <Route path="/verify-phone" element={<AuthLayout><VerifyPhone /></AuthLayout>} />
              <Route path="/verify-email" element={<AuthLayout><VerifyEmail /></AuthLayout>} />
              <Route path="/verify-device" element={<AuthLayout><VerifyDeviceLogin /></AuthLayout>} />
              <Route path="/verify-parallel" element={<AuthLayout><VerifyParallel /></AuthLayout>} />
              <Route path="/verify" element={<Navigate to="/verify-parallel" replace />} />
              <Route path="/onboarding" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />
              {/* Legacy link target (dashboard nudge banner, old verify pages) — profile completion now lives in onboarding */}
              <Route path="/complete-profile" element={<Navigate to="/onboarding?resume=true" replace />} />

              <Route path="/reset-password/:token" element={<AuthLayout><ResetPassword /></AuthLayout>} />
              <Route path="/reset-password" element={<AuthLayout><ResetPassword /></AuthLayout>} />
              {/* OAuth Callback */}
              <Route path="/oauth/callback" element={<OAuthCallback />} />

              {/* Referral short-link: gigship.com/r/CODE → /register?ref=CODE */}
              <Route path="/r/:code" element={<ReferralRedirect />} />

              {/* verify-identity is now a modal on the Settings page (not a dedicated page).
                   Legacy /verify-identity links (emails, bookmarks) redirect to Settings > Profile. */}
              <Route
                path="/verify-identity"
                element={
                  <ProtectedRoute>
                    <Navigate to="/dashboard/settings?tab=profile&openVerify=id" replace />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/settings/verification"
                element={
                  <ProtectedRoute>
                    <Navigate to="/dashboard/settings?tab=profile&openVerify=id" replace />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/verify-facial"
                element={<DashboardWrapper><FacialVerification /></DashboardWrapper>}
              />

              {/* Diagnostic - PUBLIC */}
              <Route path="/sms-diagnostic" element={<SMSDiagnostic />} />

              {/* Protected Dashboard */}
              <Route path="/gigs/create" element={<DashboardWrapper><GigCreation /></DashboardWrapper>} />
              <Route path="/dashboard/gigs/edit/:gigId" element={<DashboardWrapper><GigEdit /></DashboardWrapper>} />
              <Route path="/dashboard/requests/edit/:requestId" element={<DashboardWrapper><RequestEdit /></DashboardWrapper>} />
              <Route path="/requests/create" element={<DashboardWrapper><PostJobRequest /></DashboardWrapper>} />
              <Route path="/saved-gigs" element={<DashboardWrapper><SavedGigs /></DashboardWrapper>} />
              <Route path="/saved-requests" element={<DashboardWrapper><SavedRequests /></DashboardWrapper>} />
              {/* Issue 9: Forum — create requires auth */}
              <Route path="/forum/create" element={<DashboardWrapper><CreateThread /></DashboardWrapper>} />
              <Route path="/dashboard" element={<DashboardWrapper />}>
                <Route index element={<Dashboard />} />
                <Route path="orders/create/:gigId" element={<OrderCreation />} />
                <Route path="orders" element={<OrdersDashboard />} />
                <Route path="orders/:orderId" element={<OrderDetail />} />
                <Route path="wallet" element={<Wallet />} />
                <Route path="messages" element={<Messages />} />
                <Route path="settings" element={<Settings />} />
                <Route path="analytics" element={<Analytics />} />
                <Route path="reviews" element={<Reviews />} />
                <Route path="referrals" element={<Referrals />} />
                <Route path="gigs" element={<MyGigs />} />
                <Route path="requests" element={<MyRequests />} />
                <Route path="requests/:id/proposals" element={<RequestProposals />} />
                <Route path="notifications" element={<div className="p-8 text-center text-gray-500">Notification History (Coming Soon)</div>} />
                <Route path="earnings" element={<Navigate to="/dashboard/wallet" replace />} />
                {/* Portfolio redirect to Settings */}
                <Route path="portfolio" element={<Navigate to="/dashboard/settings?tab=portfolio" replace />} />
                <Route path="mediator" element={<MediatorDashboard />} />
                <Route path="forum" element={<ForumDashboard />} />
              </Route>

              {/* Admin Routes — Decoupled Admin Layout */}
              <Route path="/admin" element={<AdminRoute><AdminLayout /></AdminRoute>}>
                <Route index element={<AdminDashboard />} />
                <Route path="users" element={<AdminUsers />} />
                <Route path="orders" element={<AdminOrders />} />
                <Route path="gigs" element={<AdminGigs />} />
                <Route path="job-requests" element={<AdminJobRequests />} />
                <Route path="disputes" element={<AdminDisputes />} />
                <Route path="email-campaigns" element={<AdminEmailDashboard />} />
                <Route path="email-alerts" element={<AdminEmailAlerts />} />
                <Route path="featured-management" element={<FeaturedManagement />} />
                <Route path="deposits" element={<AdminDeposits />} />
                <Route path="payment-methods" element={<AdminPaymentMethods />} />
                <Route path="offers" element={<AdminOffersDiscounts />} />
                <Route path="bonus-settings" element={<AdminBonusSettings />} />
                <Route path="payout-settings" element={<PayoutSettings />} />
                <Route path="verifications" element={<AdminVerifications />} />
                <Route path="profile-change-requests" element={<AdminProfileChangeRequests />} />
                <Route path="onboarding" element={<AdminOnboarding />} />
                <Route path="referrals" element={<AdminReferrals />} />
                <Route path="analytics" element={<AdminAnalytics />} />
                <Route path="safety" element={<SafetyDashboard />} />
                <Route path="call-settings" element={<CallSettings />} />
                <Route path="super" element={<SuperAdminRoute><SuperAdminDashboard /></SuperAdminRoute>} />
                <Route path="logs" element={<SystemLogs />} />
              </Route>

              {/* 404 Route */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </ErrorBoundary>
        </Suspense>

        <Toaster position="bottom-center" reverseOrder={false} />
        <InstallPrompt />
        <CookieConsent />
      </OverlayQueueProvider>
    </HelmetProvider>
  );

}

// Global error handler for Tawk.API
if (typeof window !== 'undefined') {
  window.addEventListener('error', (event) => {
    if (event.message?.includes('Tawk_API')) {
      console.warn('⚠️ Tawk.API error caught and suppressed:', event.message);
      event.preventDefault();
      return true;
    }
  });

  window.addEventListener('unhandledrejection', (event) => {
    if (event.reason?.message?.includes('Tawk_API')) {
      console.warn('⚠️ Tawk.API promise rejection suppressed:', event.reason.message);
      event.preventDefault();
      return true;
    }
  });
}

export default Sentry.withErrorBoundary(App, {
  fallback: ({ error, resetError }) => (
    <div className="flex flex-col items-center justify-center min-h-screen p-8 text-center">
      <h1 className="text-2xl font-bold text-gray-900 mb-4">Something went wrong</h1>
      <p className="text-gray-600 mb-6">GigShip is under maintenance. We've been notified.</p>
      <button onClick={resetError} className="px-6 py-3 bg-orange-500 text-white rounded-lg">
        Try Again
      </button>
    </div>
  ),
  showDialog: false,
});